/**
 * La file du soir.
 *
 * Le dernier bloc de la maquette du tableau de bord, et le seul qui demandait
 * une décision qui n'était pas technique. Elle est prise : **la file se
 * construit quand le patron ouvre l'écran**, sur les données de l'instant ; un
 * balayage nocturne se contente de compter les signaux et de poser une alerte,
 * sans rien préparer. Voir `evening_queue_signals()` en base.
 *
 * Ce que ça évite : `pending_actions` ne vaut que quinze minutes, à dessein —
 * on ne fait pas confirmer un envoi à 500 € pour en exécuter un à 5 000 le
 * lendemain. Préparer la nuit obligerait soit à allonger cette validité, soit à
 * re-préparer à l'affichage, c'est-à-dire à tenir deux mécanismes là où un
 * suffit.
 *
 * ══════════════════════════════════════════════════════════════
 * Ce que la file peut préparer, et ce qu'elle sait seulement montrer
 * ══════════════════════════════════════════════════════════════
 * Les quatre signaux de la maquette existent tous en base. Jusqu'au
 * 14 septembre, un seul correspondait à un verbe N3 : la relance de facture.
 * Les trois autres menaient à l'écran où le geste se fait, et ce choix était
 * écrit — un verbe à effet externe se conçoit une fois, pas dans la foulée
 * d'un panneau.
 *
 * Le lot 3 du plan UI/UX les a conçus, chacun avec son résumé, son empreinte
 * et ce qu'il vérifie avant d'agir :
 *
 *   · **situation** — `facturer_et_envoyer` (N3) quand le client a une adresse,
 *     et `etablir_situation` (N2) toujours, pour qui veut relire le brouillon ;
 *   · **devis** — `relancer_devis` (N3), un courrier de relance et non un
 *     renvoi ;
 *   · **stock** — `commander_materiel` (N3). Il manque une donnée que la file
 *     n'a pas le droit d'inventer, la quantité : l'item porte donc un brouillon
 *     de commande (`stockOrder`), et l'écran demande la quantité avant de
 *     préparer.
 *
 * Quand une donnée manque — pas d'adresse, pas de fournisseur —, la ligne
 * garde le lien vers l'écran où se corrige ce qui manque.
 *
 * Tout ce fichier est pur, donc éprouvable sans base.
 */
import type { ReminderCadence, ReminderLevel } from "../config/types.js";
import { reminderLevelFor } from "../config/types.js";
import { situationAmounts, situationPercent } from "../documents/amounts.js";
import { round2 } from "../../money.js";

export type EveningItemKind = "relance" | "situation" | "devis" | "stock";

/** Une action que la file sait préparer, avec la charge exacte du verbe. */
export interface PreparableAction {
  action: string;
  payload: Record<string, unknown>;
}

/**
 * Tout ce qu'il faut pour commander un article, sauf la quantité.
 *
 * `commander_materiel` exige le fournisseur et son adresse : sans eux, le bon
 * de commande n'a nulle part où partir, et la file ne propose pas le geste.
 */
export interface StockOrderDraft {
  catalogItemId: string;
  itemName: string;
  unit: string;
  unitCost: number;
  supplierId: string;
  supplierName: string;
  supplierEmail: string;
}

export interface EveningItem {
  /** Stable d'un affichage à l'autre : `kind:identifiant de la ligne source`. */
  id: string;
  kind: EveningItemKind;
  title: string;
  detail: string;
  /** L'enjeu, quand il s'en chiffre un. */
  amount: number | null;
  /** Où le geste se fait. Toujours renseigné, préparable ou non. */
  href: string;
  /**
   * La charge d'une action confirmable, ou `null` quand le geste n'existe pas
   * encore sous forme de verbe N3. Voir l'en-tête.
   */
  prepare: PreparableAction | null;
  /**
   * Le même geste sans rien envoyer : une écriture interne (N2), exécutée
   * sans confirmation. `etablir_situation` aujourd'hui — pour le patron qui
   * veut relire la facture avant qu'elle parte.
   */
  internal: PreparableAction | null;
  /** Une commande à compléter d'une quantité. Voir `StockOrderDraft`. */
  stockOrder: StockOrderDraft | null;
  /** Plus c'est haut, plus ça passe devant. */
  weight: number;
}

/* ─── Ce que le dépôt fournit ───────────────────────────────────────────── */

export interface OverdueInvoiceSignal {
  invoiceId: string;
  invoiceNumber: string;
  clientName: string;
  remaining: number;
  daysLate: number;
  /**
   * Le palier le plus élevé réellement parti, tous canaux confondus. `0`
   * quand rien n'est encore parti.
   *
   * Lu dans `reminder_actions`, et non déduit du retard : c'est ce qui
   * empêche la file de proposer une relance que le cron de 8 h a déjà
   * expédiée le matin même.
   */
  lastReminderLevel: number;
}

export interface ProjectProgressSignal {
  projectId: string;
  projectName: string;
  /** Moyenne des phases, en points. */
  progressPercent: number;
  /** Le devis signé. Sans lui, aucun pourcentage d'avancement n'est chiffrable. */
  contractHt: number;
  contractTtc: number;
  invoicedHt: number;
  quoteId: string;
  quoteNumber: string;
  clientName: string;
  /** Sans adresse, la situation s'établit mais ne s'envoie pas d'ici. */
  clientEmail: string | null;
}

export interface PendingQuoteSignal {
  quoteId: string;
  quoteNumber: string;
  title: string;
  clientName: string;
  totalTtc: number;
  /** Jours écoulés depuis l'envoi. */
  daysSinceSent: number;
  /**
   * Durée de validité annoncée au client, en jours. C'est elle qui borne la
   * relance — voir `devis`.
   */
  validityDays: number;
  /** La date jusqu'à laquelle le prix tient, AAAA-MM-JJ, si l'envoi est daté. */
  validUntil: string | null;
  clientEmail: string | null;
  /** Jours depuis la dernière relance réellement partie, `null` si aucune. */
  daysSinceLastFollowUp: number | null;
}

export interface LowStockSignal {
  itemId: string;
  name: string;
  unit: string;
  stockQuantity: number;
  reorderThreshold: number;
  unitCost: number;
  supplierId: string | null;
  supplierName: string | null;
  supplierEmail: string | null;
}

export interface EveningSignals {
  overdueInvoices: readonly OverdueInvoiceSignal[];
  projects: readonly ProjectProgressSignal[];
  quotes: readonly PendingQuoteSignal[];
  lowStock: readonly LowStockSignal[];
  cadence: ReminderCadence;
}

/* ─── Les seuils ────────────────────────────────────────────────────────── */

/**
 * L'écart, en points, à partir duquel un chantier mérite une situation.
 *
 * Quinze points sur un chantier de 60 000 € font 9 000 € d'avance de
 * trésorerie que l'entreprise consent à son client sans l'avoir décidé. En
 * deçà, l'écart relève du décalage normal entre l'avancement d'un jour et la
 * facturation du mois — le signaler chaque soir ferait cesser de le lire.
 */
export const SITUATION_GAP_POINTS = 15;

/**
 * Après combien de jours un devis sans réponse mérite un coup de fil.
 *
 * Écrit ici, en un seul endroit, et non dans l'écran. Il n'est pas encore
 * réglable par tenant, et c'est un manque assumé plutôt qu'un oubli : un
 * réglage de plus dans Paramètres se justifiera le jour où une entreprise
 * demandera à le déplacer.
 *
 * **Il n'y a pas de constante symétrique pour la borne haute**, et c'est le
 * correctif du 10 septembre : la validité du devis la fournit. Un nombre en
 * dur y aurait été arbitraire, alors que le devis porte déjà la date au-delà
 * de laquelle il ne vaut plus.
 */
export const QUOTE_FOLLOWUP_DAYS = 7;

/* ─── La construction de la file ────────────────────────────────────────── */

/**
 * Les relances qui restent à faire ce soir.
 *
 * La question posée n'est **pas** celle du cron. Le cron demande « qui vient de
 * franchir un palier aujourd'hui » — `reminderDueAt` — parce qu'il expédie tout
 * seul et ne doit pas envoyer deux fois. La file demande « quel ton convient
 * maintenant, et l'a-t-on déjà employé » : c'est la question de l'écran, la
 * même que celle de la fiche de facture.
 *
 * Formulée ainsi, elle ne double jamais le cron : dès qu'une relance de
 * niveau 3 est partie, la facture disparaît de la file jusqu'à ce qu'elle
 * mérite le niveau 4.
 */
function relances(
  invoices: readonly OverdueInvoiceSignal[],
  cadence: ReminderCadence,
): EveningItem[] {
  const items: EveningItem[] = [];

  for (const f of invoices) {
    if (f.remaining <= 0) continue;
    const niveau = reminderLevelFor(f.daysLate, cadence);
    if (niveau <= f.lastReminderLevel) continue;
    // Le niveau 1 est un rappel avant échéance : il ne concerne pas une
    // facture déjà échue, et la file ne regarde que l'échu.
    if (f.daysLate < 0) continue;

    items.push({
      id: `relance:${f.invoiceId}`,
      kind: "relance",
      title: `Relancer ${f.clientName}`,
      detail:
        `Facture ${f.invoiceNumber}, échue depuis ${f.daysLate} jour` +
        `${f.daysLate > 1 ? "s" : ""} — ${TON[niveau]}`,
      amount: f.remaining,
      href: `/factures/${f.invoiceId}`,
      // Le seul des quatre signaux qui corresponde à un verbe N3 inscrit au
      // registre. La charge porte le résumé que le patron lira : c'est le
      // service qui l'écrit, pas cet objet — voir `describeAction`.
      prepare: {
        action: "relancer_facture",
        payload: {
          invoiceId: f.invoiceId,
          level: niveau,
          invoiceNumber: f.invoiceNumber,
          recipient: f.clientName,
          amountDue: f.remaining,
        },
      },
      internal: null,
      stockOrder: null,
      // Une mise en demeure passe devant un rappel amical, et le montant
      // départage à ton égal.
      weight: 1000 + niveau * 100 + Math.min(99, f.remaining / 1000),
    });
  }

  return items;
}

const TON: Record<ReminderLevel, string> = {
  1: "rappel courtois",
  2: "relance amicale",
  3: "relance ferme",
  4: "mise en demeure",
};

/** Les chantiers qui ont avancé plus vite qu'ils n'ont été facturés. */
function situations(projects: readonly ProjectProgressSignal[]): EveningItem[] {
  const items: EveningItem[] = [];

  for (const p of projects) {
    if (p.contractHt <= 0) continue;
    const factureEnPoints = (p.invoicedHt / p.contractHt) * 100;
    const ecart = p.progressPercent - factureEnPoints;
    if (ecart < SITUATION_GAP_POINTS) continue;

    // Le pourcentage arrondi d'abord, le montant ensuite : c'est l'ordre dans
    // lequel la fonction Edge calculera, et `facturer_et_envoyer` refuse
    // d'envoyer une facture qui ne réclame pas le montant annoncé.
    const percent = situationPercent(ecart);
    const { amountHt: aFacturer, amountTtc } = situationAmounts(
      { totalHt: p.contractHt, totalTtc: p.contractTtc },
      percent,
    );
    items.push({
      id: `situation:${p.projectId}`,
      kind: "situation",
      title: `Établir une situation — ${p.projectName}`,
      detail:
        `Avancé à ${Math.round(p.progressPercent)} %, facturé à ` +
        `${Math.round(factureEnPoints)} % : ${Math.round(ecart)} points d'avance ` +
        `consentie au client`,
      amount: aFacturer,
      href: `/chantiers/${p.projectId}`,
      prepare: p.clientEmail
        ? {
            action: "facturer_et_envoyer",
            payload: {
              quoteId: p.quoteId,
              type: "situation",
              percent,
              quoteNumber: p.quoteNumber,
              recipient: p.clientName,
              recipientEmail: p.clientEmail,
              amountHt: aFacturer,
              amountTtc,
            },
          }
        : null,
      internal: { action: "etablir_situation", payload: { quoteId: p.quoteId, percent } },
      stockOrder: null,
      weight: 900 + Math.min(99, aFacturer / 1000),
    });
  }

  return items;
}

/**
 * Les devis partis, restés sans réponse, et **encore valables**.
 *
 * La borne haute est le correctif du 10 septembre, trouvé en ouvrant l'écran :
 * la file proposait de relancer un devis envoyé 169 jours plus tôt et expiré
 * depuis 139. C'est exactement le piège que `reminderDueAt` documente pour les
 * factures — franchir un seuil, et non l'avoir dépassé — reproduit ici sans
 * qu'aucun test ne le voie, parce qu'aucun test n'avait imaginé un devis aussi
 * vieux.
 *
 * Passé sa validité, un devis n'attend plus de réponse : le prix n'engage plus
 * l'entreprise, et le geste n'est pas « relancer » mais « redeviser » ou
 * « classer sans suite ». Le laisser dans la file le met devant les yeux du
 * patron chaque soir jusqu'à la fin des temps, ce qui est le meilleur moyen
 * qu'il cesse de lire la file.
 *
 * Conséquence assumée : un devis dont la validité est plus courte que le délai
 * de relance ne sera jamais propose. Une validité de moins d'une semaine ne
 * laisse de toute façon pas la place à un rythme « on attend, puis on
 * appelle ».
 */
function devis(quotes: readonly PendingQuoteSignal[]): EveningItem[] {
  return quotes
    .filter(
      (q) =>
        q.daysSinceSent >= QUOTE_FOLLOWUP_DAYS &&
        q.daysSinceSent <= q.validityDays &&
        // Relancé il y a moins d'une semaine : on laisse au client le temps
        // de répondre à la relance avant de lui en envoyer une autre. Le même
        // délai qu'après l'envoi, pour la même raison.
        (q.daysSinceLastFollowUp == null || q.daysSinceLastFollowUp >= QUOTE_FOLLOWUP_DAYS),
    )
    .map<EveningItem>((q) => {
      const restants = q.validityDays - q.daysSinceSent;
      return {
        id: `devis:${q.quoteId}`,
        kind: "devis",
        title: `Relancer ${q.clientName}`,
        // La validité restante plutôt que le seul âge : c'est elle qui dit
        // l'urgence. « Sans réponse depuis 12 jours » n'appelle rien de
        // précis ; « valable encore 3 jours » appelle un appel aujourd'hui.
        detail:
          `Devis ${q.quoteNumber} envoyé il y a ${q.daysSinceSent} jours — ` +
          (restants === 0
            ? "expire aujourd'hui"
            : `valable encore ${restants} jour${restants > 1 ? "s" : ""}`) +
          (q.daysSinceLastFollowUp != null
            ? ` · déjà relancé il y a ${q.daysSinceLastFollowUp} jours`
            : ""),
        amount: q.totalTtc,
        href: `/devis/${q.quoteId}`,
        // Relancer n'est pas renvoyer : `relancer_devis` écrit un autre
        // courrier et ne touche pas au devis. Sans adresse, le geste reste le
        // coup de fil — la file mène au devis, où se trouve le client.
        prepare: q.clientEmail
          ? {
              action: "relancer_devis",
              payload: {
                quoteId: q.quoteId,
                quoteNumber: q.quoteNumber,
                recipient: q.clientName,
                recipientEmail: q.clientEmail,
                amountTtc: q.totalTtc,
                validUntil: q.validUntil,
              },
            }
          : null,
        internal: null,
        stockOrder: null,
        // Le devis qui va expirer passe devant celui qui a du temps : à montant
        // égal, c'est la fenêtre qui se ferme qui décide.
        weight: 800 + Math.max(0, 40 - restants) + Math.min(50, q.totalTtc / 1000),
      };
    });
}

/** Les articles passés sous leur seuil de réapprovisionnement. */
function stock(items: readonly LowStockSignal[]): EveningItem[] {
  return items
    .filter((a) => a.stockQuantity <= a.reorderThreshold)
    .map<EveningItem>((a) => ({
      id: `stock:${a.itemId}`,
      kind: "stock",
      title: `Commander ${a.name}`,
      detail:
        `${a.stockQuantity} ${a.unit} en stock, sous le seuil de ` +
        `${a.reorderThreshold} ${a.unit}`,
      amount: null,
      // Sans fournisseur joignable, le panier sur le bon article : c'est de là
      // qu'on choisit à qui commander.
      href: `/commandes?article=${a.itemId}`,
      prepare: null,
      internal: null,
      stockOrder:
        a.supplierId && a.supplierName && a.supplierEmail
          ? {
              catalogItemId: a.itemId,
              itemName: a.name,
              unit: a.unit,
              unitCost: a.unitCost,
              supplierId: a.supplierId,
              supplierName: a.supplierName,
              supplierEmail: a.supplierEmail,
            }
          : null,
      // Une rupture passe devant un stock simplement bas : à zéro, le chantier
      // s'arrête demain matin.
      weight: a.stockQuantity <= 0 ? 950 : 700,
    }));
}

/**
 * La file, assemblée et classée.
 *
 * Le classement mêle les quatre natures plutôt que de les grouper : ce qui
 * décide de l'ordre, c'est l'urgence du geste, pas la table dont il sort. Une
 * mise en demeure de 12 000 € passe devant une commande de vis, et c'est bien
 * la question que le patron se pose en fin de journée.
 */
export function buildEveningQueue(signals: EveningSignals): EveningItem[] {
  return [
    ...relances(signals.overdueInvoices, signals.cadence),
    ...situations(signals.projects),
    ...devis(signals.quotes),
    ...stock(signals.lowStock),
  ].sort((a, b) => b.weight - a.weight || a.id.localeCompare(b.id));
}

/** Ce que la file représente en argent, pour l'annoncer d'un coup d'œil. */
export function queueAmount(items: readonly EveningItem[]): number {
  return round2(items.reduce((s, i) => s + (i.amount ?? 0), 0));
}

/* ─── Le moment de la journée ───────────────────────────────────────────── */

export type MomentDeLaJournee = "matin" | "apres_midi" | "soir";

/**
 * À quel moment de la journée on est.
 *
 * Le patron : *« "ce qui vous attend ce soir" doit être dynamique »* — le
 * matin jusqu'à 11 h, l'après-midi jusqu'à 16 h, le soir ensuite. Ce sont ses
 * bornes, pas une convention : elles suivent la journée d'un artisan, qui
 * ouvre l'application avant de partir, la rouvre après le repas et fait ses
 * papiers le soir.
 *
 * Le titre comptait plus qu'il n'y paraît. Un panneau qui dit « ce soir » à
 * 7 h du matin se lit comme « pas maintenant », et c'est exactement le
 * contraire de ce que la file demande.
 *
 * La fonction est ici et non dans l'écran : c'est une règle, elle se teste, et
 * le prochain écran qui veut saluer le patron au bon moment doit lire la même.
 * Sur 11 h 00 pile, c'est encore le matin — « entre 00 h et 11 h » l'inclut.
 */
export function momentDeLaJournee(now: Date = new Date()): MomentDeLaJournee {
  const minutes = now.getHours() * 60 + now.getMinutes();
  if (minutes <= 11 * 60) return "matin";
  if (minutes <= 16 * 60) return "apres_midi";
  return "soir";
}

/** « Ce qui vous attend ce matin », et les deux autres. */
export function eveningQueueTitle(now: Date = new Date()): string {
  switch (momentDeLaJournee(now)) {
    case "matin":
      return "Ce qui vous attend ce matin";
    case "apres_midi":
      return "Ce qui vous attend cet après-midi";
    default:
      return "Ce qui vous attend ce soir";
  }
}
